import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Paper, Typography, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Box, IconButton, Chip, Dialog, DialogTitle, DialogContent, TextField,
  Tabs, Tab
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import { motion } from 'framer-motion';
import Page from '../../../components/page/Page';
import Spinner from '../../../components/Spinner';
import useThemeMediaQuery from '../../../../core/hooks/useThemeMediaQuery';
import apiService from '../../../../core/services/api-client';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import 'moment/locale/es';
import {
  ReportsDashboardResponse,
  getReportsDashboard,
  getInventoryAgeDetail,
  InventoryAgeDetailResponse,
  getRotationDetail,
  RotationDetailResponse,
  getProductsDetail,
  ProductsDetailResponse,
  InventoryEvolutionResponse,
  getInventoryEvolution,
} from '../../../../core/modules/reportsDashboard/services/api';
import moment from 'moment';

// Formateo CLP
const fmtCLP = (v: number) => '$' + Math.round(v).toLocaleString('es-CL');
const fmtCLPShort = (v: number) => {
  if (v >= 1000000000) return '$' + (v / 1000000000).toFixed(1) + 'B';
  if (v >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M';
  if (v >= 1000) return '$' + (v / 1000).toFixed(1) + 'K';
  if (v === 0) return '$0';
  return '$' + v;
};
const fmtNum = (v: number) => Math.round(v).toLocaleString('es-CL');

// Animaciones
const container = { show: { transition: { staggerChildren: 0.06 } } };
const item = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };

// Colores de riesgos
const riskColors: Record<string, string> = {
  'Stock Cero': '#f44336',
  'Stock Crítico': '#ff9800',
  'Sobre Stock': '#ffc107',
  'Baja Rotación': '#2196f3',
  'Compra Sugerida': '#4caf50',
  'Devoluciones': '#9c27b0',
  'Ajuste de Stock': '#9e9e9e',
  'Ventas sin Stock': '#b71c1c',
};

// Color por días en alerta
const daysColor = (days: number): string => {
  if (days > 90) return '#f44336';
  if (days > 60) return '#e65100';
  if (days > 30) return '#ff9800';
  if (days > 7) return '#8bc34a';
  return '#4caf50';
};

// Metric ID mapping
const metricIdMap: Record<string, string> = {
  'Stock Cero': 'STOCK_CERO',
  'Stock Crítico': 'STOCK_CRITICO',
  'Sobre Stock': 'SOBRE_STOCK',
  'Baja Rotación': 'BAJA_ROTACION',
};

// Tarjeta KPI
const KpiCard: React.FC<{ label: string; value: string; icon: string; color?: string }> = ({ label, value, icon, color }) => (
  <motion.div variants={item} style={{ height: '100%' }}>
    <Paper className="p-16 flex flex-col items-center justify-center shadow rounded-2xl" sx={{ minHeight: 120, height: '100%' }}>
      <Typography sx={{ fontSize: 28 }}>{icon}</Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 700, color: color || 'text.primary', mt: 0.5 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>{label}</Typography>
    </Paper>
  </motion.div>
);

// Título de sección
const SectionTitle: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => (
  <Box sx={{ mb: 2, mt: 3 }}>
    <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{title}</Typography>
    <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
  </Box>
);

const ReportsDashboard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const isMobile = useThemeMediaQuery((t: any) => t.breakpoints.down('lg'));

  const [data, setData] = useState<ReportsDashboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filtros
  const [warehouse, setWarehouse] = useState('');
  const [category, setCategory] = useState('');
  const [supplier, setSupplier] = useState('');
  const [startDate, setStartDate] = useState<any>(moment().subtract(1, 'days'));
  const [endDate, setEndDate] = useState<any>(moment().subtract(1, 'days'));

  // Tab activo
  const [activeTab, setActiveTab] = useState(0);

  // Evolución alertas
  const [alertEvolution, setAlertEvolution] = useState<{ date: string; unique_skus_in_alert: number }[]>([]);

  // Paginación riesgos
  const [riskPage, setRiskPage] = useState(0);

  // Modal detalle edad inventario
  const [ageDetailOpen, setAgeDetailOpen] = useState(false);
  const [ageDetailData, setAgeDetailData] = useState<InventoryAgeDetailResponse | null>(null);
  const [ageDetailLoading, setAgeDetailLoading] = useState(false);
  const [ageDetailSearch, setAgeDetailSearch] = useState('');

  // Modal detalle rotación
  const [rotDetailOpen, setRotDetailOpen] = useState(false);
  const [rotDetailData, setRotDetailData] = useState<RotationDetailResponse | null>(null);
  const [rotDetailLoading, setRotDetailLoading] = useState(false);
  const [rotDetailSearch, setRotDetailSearch] = useState('');

  // Modal detalle productos
  const [prodDetailOpen, setProdDetailOpen] = useState(false);
  const [prodDetailData, setProdDetailData] = useState<ProductsDetailResponse | null>(null);
  const [prodDetailLoading, setProdDetailLoading] = useState(false);
  const [prodDetailSearch, setProdDetailSearch] = useState('');

  // Evolución del inventario
  const [evolutionData, setEvolutionData] = useState<InventoryEvolutionResponse | null>(null);
  const [evolutionLoading, setEvolutionLoading] = useState(false);
  const [evolutionDays, setEvolutionDays] = useState(90);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const params: any = {};
      if (warehouse) params.warehouse = warehouse;
      if (category) params.category = category;
      if (supplier) params.supplier = supplier;
      if (startDate) params.start_date = startDate.format('YYYY-MM-DD');
      if (endDate) params.end_date = endDate.format('YYYY-MM-DD');
      const res = await getReportsDashboard(id, params);
      setData(res);
    } catch {
      setError('Error al cargar el dashboard de reportes');
    } finally {
      setLoading(false);
    }
  }, [id, warehouse, category, supplier, startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAgeDetail = async (metricName: string) => {
    const metricId = metricIdMap[metricName];
    if (!metricId || !id) return;
    setAgeDetailOpen(true);
    setAgeDetailLoading(true);
    setAgeDetailSearch('');
    try {
      const res = await getInventoryAgeDetail(id, metricId, startDate ? startDate.format('YYYY-MM-DD') : undefined);
      setAgeDetailData(res);
    } catch {
      setAgeDetailData(null);
    } finally {
      setAgeDetailLoading(false);
    }
  };

  const openRotationDetail = async (sortType: 'fastest' | 'slowest') => {
    if (!id) return;
    setRotDetailOpen(true);
    setRotDetailLoading(true);
    setRotDetailSearch('');
    try {
      const p: any = {};
      if (warehouse) p.warehouse = warehouse;
      if (category) p.category = category;
      if (supplier) p.supplier = supplier;
      const res = await getRotationDetail(id, sortType, p);
      setRotDetailData(res);
    } catch {
      setRotDetailData(null);
    } finally {
      setRotDetailLoading(false);
    }
  };

  const openProductsDetail = async (sortType: 'by_value' | 'by_quantity') => {
    if (!id) return;
    setProdDetailOpen(true);
    setProdDetailLoading(true);
    setProdDetailSearch('');
    try {
      const p: any = {};
      if (warehouse) p.warehouse = warehouse;
      if (category) p.category = category;
      if (supplier) p.supplier = supplier;
      const res = await getProductsDetail(id, sortType, p);
      setProdDetailData(res);
    } catch {
      setProdDetailData(null);
    } finally {
      setProdDetailLoading(false);
    }
  };

  // Cargar evolución de alertas
  useEffect(() => {
    if (!id) return;
    const api = apiService();
    api.get(`/metrics/${id}/alert-evolution`)
      .then((res) => setAlertEvolution((res.data.data || []).filter((d: any) => d.unique_skus_in_alert > 0)))
      .catch(() => {});
  }, [id]);

  // Cargar evolución del inventario (lazy: solo cuando tab 1 está activo)
  useEffect(() => {
    if (activeTab !== 1 || !id) return;
    setEvolutionLoading(true);
    getInventoryEvolution(id, evolutionDays)
      .then((res) => setEvolutionData(res))
      .catch(() => setEvolutionData(null))
      .finally(() => setEvolutionLoading(false));
  }, [activeTab, id, evolutionDays]);

  if (loading && !data) {
    return (
      <Page content={
        <div className="flex items-center justify-center w-full" style={{ minHeight: 400 }}>
          <Spinner show={true} color="#000" />
          <span className="ml-8">Cargando dashboard...</span>
        </div>
      } scroll={isMobile ? 'normal' : 'content'} />
    );
  }

  if (error && !data) {
    return (
      <Page content={
        <div className="p-24 text-center">
          <Typography color="error">{error}</Typography>
        </div>
      } scroll={isMobile ? 'normal' : 'content'} />
    );
  }

  if (!data) return null;

  const { general, economic, health, transit, distribution, rotation, filters, inventory_age } = data;

  // --- Gráficos config ---
  const barChartCategories = (economic.top_categories_by_value || []).slice(0, 10);
  const barChartSuppliers = economic.top_suppliers_by_value || [];

  const categoriesBarOptions: any = {
    chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit', foreColor: 'inherit' },
    plotOptions: { bar: { horizontal: true, barHeight: '60%' } },
    xaxis: {
      categories: barChartCategories.map((c) => c.category),
      labels: { formatter: (v: number) => window.innerWidth < 768 ? fmtCLPShort(v) : fmtCLP(v), style: { colors: theme.palette.text.secondary, fontSize: window.innerWidth < 768 ? '10px' : '12px' } },
    },
    yaxis: { labels: { style: { colors: theme.palette.text.secondary, fontSize: window.innerWidth < 768 ? '10px' : '12px' } } },
    tooltip: { y: { formatter: (v: number) => fmtCLP(v) } },
    colors: [theme.palette.primary.main],
    dataLabels: { enabled: false },
    grid: { borderColor: theme.palette.divider },
  };

  const suppliersBarOptions: any = {
    chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit', foreColor: 'inherit' },
    plotOptions: { bar: { horizontal: true, barHeight: '60%' } },
    xaxis: {
      categories: barChartSuppliers.map((s) => s.supplier),
      labels: { formatter: (v: number) => window.innerWidth < 768 ? fmtCLPShort(v) : fmtCLP(v), style: { colors: theme.palette.text.secondary, fontSize: window.innerWidth < 768 ? '10px' : '12px' } },
    },
    yaxis: { labels: { style: { colors: theme.palette.text.secondary, fontSize: window.innerWidth < 768 ? '10px' : '12px' } } },
    tooltip: { y: { formatter: (v: number) => fmtCLP(v) } },
    colors: [theme.palette.secondary.main],
    dataLabels: { enabled: false },
    grid: { borderColor: theme.palette.divider },
  };

  // Evolución alertas chart
  const alertLabels = alertEvolution.map((d) => moment(d.date).format('DD/MM/YYYY'));
  const alertLineOptions: any = {
    chart: { type: 'line', toolbar: { show: false }, fontFamily: 'inherit', foreColor: 'inherit', zoom: { enabled: false } },
    colors: [theme.palette.primary.main],
    labels: alertLabels,
    dataLabels: { enabled: false },
    stroke: { width: 3, curve: 'smooth' },
    xaxis: {
      labels: { style: { colors: theme.palette.text.secondary }, rotate: -45, hideOverlappingLabels: true },
      tickAmount: Math.min(12, alertEvolution.length),
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: { style: { colors: theme.palette.text.secondary }, formatter: (v: number) => Math.round(v).toLocaleString() },
      title: { text: 'SKUs en Alerta' },
    },
    tooltip: { followCursor: true, theme: theme.palette.mode },
    grid: { borderColor: theme.palette.divider },
  };

  // Distribución por categoría (pie)
  const catPieData = (distribution.stock_by_category || []).slice(0, 10);
  const catPieOptions: any = {
    chart: { type: 'donut', fontFamily: 'inherit', foreColor: 'inherit' },
    labels: catPieData.map((c) => c.category),
    legend: { position: 'bottom' },
    tooltip: { y: { formatter: (v: number) => fmtCLP(v) } },
    theme: { monochrome: { enabled: true, color: theme.palette.info.main, shadeIntensity: 0.75, shadeTo: 'light' } },
  };

  return (
    <Page
      content={
        <div className="w-full p-24">
          <Typography className="mb-16 text-3xl font-bold leading-tight tracking-tight">
            Dashboard de Reportes
          </Typography>

          {/* FILTROS */}
          <Paper className="flex items-center flex-wrap gap-16 p-16 mb-24">
            {filters.warehouses.length > 1 && (
              <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Bodega</InputLabel>
                <Select
                  label="Bodega"
                  value={warehouse}
                  onChange={(e) => setWarehouse(e.target.value)}
                  endAdornment={warehouse ? (
                    <IconButton size="small" sx={{ mr: 2 }} onClick={() => setWarehouse('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {filters.warehouses.map((w) => <MenuItem key={w} value={w}>{w}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Categoría</InputLabel>
              <Select
                label="Categoría"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                endAdornment={category ? (
                  <IconButton size="small" sx={{ mr: 2 }} onClick={() => setCategory('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : null}
              >
                <MenuItem value="">Todas</MenuItem>
                {filters.categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            {filters.suppliers.length > 0 && (
              <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Proveedor</InputLabel>
                <Select
                  label="Proveedor"
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  endAdornment={supplier ? (
                    <IconButton size="small" sx={{ mr: 2 }} onClick={() => setSupplier('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {filters.suppliers.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="es">
              <DatePicker
                label="Inicio"
                format="DD/MM/YYYY"
                views={['year', 'month', 'day']}
                value={startDate}
                onChange={setStartDate}
                slotProps={{ textField: { size: 'small', sx: { width: 140 } } }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="es">
              <DatePicker
                label="Fin"
                format="DD/MM/YYYY"
                views={['year', 'month', 'day']}
                value={endDate}
                onChange={setEndDate}
                slotProps={{ textField: { size: 'small', sx: { width: 140 } } }}
              />
            </LocalizationProvider>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', ml: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(_e, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, px: 1.5, fontSize: 13 } }}
              >
                <Tab label="Panel Económico" />
                <Tab label="Edad del Inventario" />
                <Tab label="Compras en Tránsito" />
                <Tab label="Rotación del Inventario" />
              </Tabs>
            </Box>
            {loading && <Spinner show={true} color="#000" />}
          </Paper>

          {/* SECCIÓN 1: Estado General (Tab 0: Panel Económico) */}
          {activeTab === 0 && (
            <>
          <SectionTitle title="Estado General del Inventario" subtitle="Indicadores clave del inventario" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24">
            {/* KPIs izquierda */}
            <motion.div className="grid grid-cols-2 gap-16" style={{ gridTemplateRows: '1fr 1fr' }} variants={container} initial="hidden" animate="show">
              <motion.div variants={item} style={{ gridRow: '1 / 3' }}>
                <Paper className="p-16 flex flex-col items-center justify-center shadow rounded-2xl h-full">
                  <Typography sx={{ fontSize: 28 }}>💰</Typography>
                  <Typography sx={{ fontSize: 28, fontWeight: 700, mt: 0.5 }}>{fmtCLP(general.inventory_value)}</Typography>
                  <Typography variant="caption" color="text.secondary">Valor del Inventario</Typography>
                </Paper>
              </motion.div>
              <KpiCard label="Total SKUs" value={fmtNum(general.total_skus)} icon="📦" />
              <KpiCard label="Unidades en Stock" value={fmtNum(general.total_units)} icon="📊" />
            </motion.div>

            {/* Riesgos del Inventario derecha */}
            <Paper className="shadow rounded-2xl" sx={{ p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Riesgos del Inventario</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {health.risks && health.risks.length > 4 && (
                      <>
                        <IconButton size="small" disabled={riskPage === 0} onClick={() => setRiskPage(0)} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, width: 28, height: 28 }}>
                          <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" disabled={riskPage === 1} onClick={() => setRiskPage(1)} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, width: 28, height: 28 }}>
                          <ChevronRightIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Box>
                {health.risks && health.risks.length > 0 ? (
                  <>
                    {/* Header */}
                    <Box sx={{ display: 'flex', px: 1.5, mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ flex: 2, fontWeight: 600 }}>Riesgo</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flex: 1, textAlign: 'center', fontWeight: 600 }}>SKUs</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flex: 1, textAlign: 'center', fontWeight: 600 }}>Valor</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flex: 1, textAlign: 'right', fontWeight: 600 }}>Acción</Typography>
                    </Box>
                    {/* Rows */}
                    {health.risks.slice(riskPage * 4, riskPage * 4 + 4).map((r, i) => (
                      <Box key={r.risk} sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1.5, borderRadius: 2, bgcolor: i % 2 === 0 ? 'action.hover' : 'transparent', mb: 0.5 }}>
                        <Box sx={{ flex: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: riskColors[r.risk] || '#9e9e9e', flexShrink: 0 }} />
                          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{r.risk}</Typography>
                        </Box>
                        <Typography sx={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 14 }}>{fmtNum(r.skus)}</Typography>
                        <Typography sx={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 14 }}>{fmtCLPShort(r.value)}</Typography>
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                          <Chip
                            label={r.action}
                            size="small"
                            sx={{
                              bgcolor: riskColors[r.risk] || '#9e9e9e',
                              color: '#fff',
                              fontWeight: 600,
                              fontSize: 12,
                              height: 26,
                              cursor: 'default',
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    Sin datos de riesgos disponibles
                  </Typography>
                )}
              </Box>
              {/* Total al pie */}
              {health.risks && health.risks.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1.5, mt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Total</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#f44336' }}>
                    {fmtCLPShort(health.risks.reduce((acc, r) => acc + r.value, 0))}
                  </Typography>
                </Box>
              )}
            </Paper>
          </div>

          {/* SECCIÓN 2: Panel Económico */}
          <SectionTitle title="Panel Económico" subtitle="¿Cuánto dinero hay en el inventario?" />
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-16 mb-16" variants={container} initial="hidden" animate="show">
            <KpiCard label="Valor Total Inventario" value={fmtCLP(economic.inventory_value)} icon="💵" />
            <KpiCard label="Valor Promedio por SKU" value={fmtCLP(economic.avg_value_per_sku)} icon="🏷️" />
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-16">
            <Paper className="p-16 shadow rounded-2xl">
              <Typography className="font-bold mb-8" color="text.secondary">Top 10 Categorías por Valor</Typography>
              <ReactApexChart options={categoriesBarOptions} series={[{ name: 'Valor', data: barChartCategories.map((c) => c.value) }]} type="bar" height={350} />
            </Paper>
            {barChartSuppliers.length > 0 && (
              <Paper className="p-16 shadow rounded-2xl">
                <Typography className="font-bold mb-8" color="text.secondary">Top Proveedores por Valor</Typography>
                <ReactApexChart options={suppliersBarOptions} series={[{ name: 'Valor', data: barChartSuppliers.map((s) => s.value) }]} type="bar" height={350} />
              </Paper>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24">
            {/* Top 10 por Valor */}
            <Paper className="shadow rounded-2xl" sx={{ overflow: 'hidden' }}>
              <Typography className="font-bold p-16 pb-0" color="text.secondary">Top 10 Productos por Valor</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Producto</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Stock</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Costo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Valor Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(distribution.top_products_by_value || []).map((p) => (
                    <TableRow key={p.sku}>
                      <TableCell>{p.sku}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</TableCell>
                      <TableCell align="right">{fmtNum(p.stock)}</TableCell>
                      <TableCell align="right">{fmtCLP(p.cost)}</TableCell>
                      <TableCell align="right">{fmtCLP(p.value)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box sx={{ p: 1.5, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }} onClick={() => openProductsDetail('by_value')}>
                  Ver detalle completo →
                </Typography>
              </Box>
            </Paper>
            {/* Top 10 por Cantidad */}
            <Paper className="shadow rounded-2xl" sx={{ overflow: 'hidden' }}>
              <Typography className="font-bold p-16 pb-0" color="text.secondary">Top 10 Productos por Cantidad</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Producto</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Stock</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(distribution.top_products_by_quantity || []).map((p) => (
                    <TableRow key={p.sku}>
                      <TableCell>{p.sku}</TableCell>
                      <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</TableCell>
                      <TableCell align="right">{fmtNum(p.stock)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box sx={{ p: 1.5, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }} onClick={() => openProductsDetail('by_quantity')}>
                  Ver detalle completo →
                </Typography>
              </Box>
            </Paper>
          </div>
            </>
          )}

          {/* SECCIÓN: Edad del Inventario (Tab 1) */}
          {activeTab === 1 && inventory_age && inventory_age.summary && inventory_age.summary.length > 0 && (
            <>
              <SectionTitle title="Edad del Inventario" subtitle="¿Cuánto tiempo llevan los productos en alerta?" />
              {/* Gráfico de barras apiladas */}
              <Paper className="p-16 shadow rounded-2xl mb-16">
                <ReactApexChart
                  options={{
                    chart: { type: 'bar', stacked: true, toolbar: { show: false }, fontFamily: 'inherit', foreColor: 'inherit' },
                    plotOptions: { bar: { horizontal: true, barHeight: '55%' } },
                    xaxis: { categories: inventory_age.summary.map((a) => a.metric), labels: { style: { colors: theme.palette.text.secondary } } },
                    yaxis: { labels: { style: { colors: theme.palette.text.secondary, fontWeight: 600 } } },
                    colors: ['#4caf50', '#ffeb3b', '#ff9800', '#e65100', '#f44336'],
                    legend: { position: 'top', horizontalAlign: 'center' },
                    dataLabels: { enabled: true, style: { fontSize: '11px', fontWeight: 600 } },
                    grid: { borderColor: theme.palette.divider },
                    tooltip: { y: { formatter: (v: number) => v + ' SKUs' } },
                  }}
                  series={[
                    { name: '1-7 días', data: inventory_age.summary.map((a) => a.ranges['1_7d']) },
                    { name: '8-30 días', data: inventory_age.summary.map((a) => a.ranges['8_30d']) },
                    { name: '31-60 días', data: inventory_age.summary.map((a) => a.ranges['31_60d']) },
                    { name: '61-90 días', data: inventory_age.summary.map((a) => a.ranges['61_90d']) },
                    { name: '90+ días', data: inventory_age.summary.map((a) => a.ranges['90_plus']) },
                  ]}
                  type="bar"
                  height={250}
                />
              </Paper>
              {/* Tabla resumen */}
              <TableContainer component={Paper} className="shadow rounded-2xl mb-16">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Métrica</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Detalle</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#4caf50' }}>1-7 días</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#f9a825' }}>8-30 días</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#ff9800' }}>31-60 días</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#e65100' }}>61-90 días</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#f44336' }}>90+ días</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inventory_age.summary.map((a) => (
                      <TableRow key={a.metric}>
                        <TableCell sx={{ fontWeight: 600 }}>{a.metric}</TableCell>
                        <TableCell>
                          <Typography
                            sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main', cursor: 'pointer', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}
                            onClick={() => openAgeDetail(a.metric)}
                          >
                            Ver detalle →
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{fmtNum(a.ranges['1_7d'])}</TableCell>
                        <TableCell align="right">{fmtNum(a.ranges['8_30d'])}</TableCell>
                        <TableCell align="right">{fmtNum(a.ranges['31_60d'])}</TableCell>
                        <TableCell align="right">{fmtNum(a.ranges['61_90d'])}</TableCell>
                        <TableCell align="right">{fmtNum(a.ranges['90_plus'])}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtNum(a.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Gráfico Evolución del Inventario */}
              <SectionTitle title="Evolución del Inventario" subtitle="¿Cómo ha cambiado tu inventario en el tiempo?" />
              <Paper className="p-16 shadow rounded-2xl mb-16">
                {/* Selector de período */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, gap: 1 }}>
                  {[30, 60, 90].map((d) => (
                    <Chip
                      key={d}
                      label={`${d} días`}
                      size="small"
                      variant={evolutionDays === d ? 'filled' : 'outlined'}
                      color={evolutionDays === d ? 'primary' : 'default'}
                      onClick={() => setEvolutionDays(d)}
                      sx={{ cursor: 'pointer', fontWeight: evolutionDays === d ? 700 : 400 }}
                    />
                  ))}
                </Box>
                {evolutionLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                    <Spinner show={true} color="#000" />
                    <Typography sx={{ ml: 1 }} color="text.secondary">Cargando evolución...</Typography>
                  </Box>
                ) : evolutionData && evolutionData.evolution.length > 0 ? (
                  <ReactApexChart
                    options={{
                      chart: { type: 'line', toolbar: { show: false }, fontFamily: 'inherit', foreColor: 'inherit', zoom: { enabled: false } },
                      stroke: { curve: 'smooth', width: [3, 3] },
                      colors: ['#2196f3', '#ff9800'],
                      xaxis: {
                        categories: evolutionData.evolution.map((e) => moment(e.date).format('DD/MM')),
                        labels: { style: { colors: theme.palette.text.secondary, fontSize: '11px' }, rotate: -45, rotateAlways: false },
                        tickAmount: isMobile ? 8 : 15,
                      },
                      yaxis: [
                        {
                          title: { text: 'Valor del Inventario', style: { color: '#2196f3', fontWeight: 600 } },
                          labels: {
                            style: { colors: '#2196f3' },
                            formatter: (v: number) => {
                              if (v >= 1e9) return '$' + (v / 1e9).toFixed(1) + 'B';
                              if (v >= 1e6) return '$' + (v / 1e6).toFixed(0) + 'M';
                              if (v >= 1e3) return '$' + (v / 1e3).toFixed(0) + 'K';
                              return '$' + v;
                            },
                          },
                        },
                        {
                          opposite: true,
                          title: { text: 'Unidades Vendidas', style: { color: '#ff9800', fontWeight: 600 } },
                          labels: { style: { colors: '#ff9800' } },
                        },
                      ],
                      legend: { position: 'bottom', horizontalAlign: 'center', fontWeight: 600 },
                      grid: { borderColor: theme.palette.divider, strokeDashArray: 4 },
                      tooltip: {
                        shared: true,
                        intersect: false,
                        x: {
                          formatter: (_: any, opts: any) => {
                            const idx = opts?.dataPointIndex;
                            if (idx != null && evolutionData.evolution[idx]) {
                              return moment(evolutionData.evolution[idx].date).locale('es').format('DD [de] MMMM, YYYY');
                            }
                            return '';
                          },
                        },
                        y: {
                          formatter: (v: number, opts: any) => {
                            const idx = opts?.dataPointIndex;
                            const seriesIdx = opts?.seriesIndex;
                            if (seriesIdx === 0) return fmtCLP(v);
                            if (seriesIdx === 1) return fmtNum(v) + ' uds';
                            return String(v);
                          },
                        },
                        custom: ({ series, seriesIndex, dataPointIndex, w }: any) => {
                          const day = evolutionData.evolution[dataPointIndex];
                          if (!day) return '';
                          return `<div style="padding:10px;font-size:13px;line-height:1.6">
                            <b>${moment(day.date).locale('es').format('DD [de] MMMM, YYYY')}</b><br/>
                            💰 Valor Inventario: <b>${fmtCLP(day.inventory_value)}</b><br/>
                            📦 Unidades en Stock: <b>${fmtNum(day.total_units)}</b><br/>
                            🛒 Vendido: <b>${fmtNum(day.total_sold)} uds</b><br/>
                            ↩️ Devuelto: <b>${fmtNum(day.total_returned)} uds</b>
                          </div>`;
                        },
                      },
                    }}
                    series={[
                      { name: 'Valor del Inventario', data: evolutionData.evolution.map((e) => Math.round(e.inventory_value)) },
                      { name: 'Unidades Vendidas', data: evolutionData.evolution.map((e) => e.total_sold) },
                    ]}
                    type="line"
                    height={380}
                  />
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No hay datos de evolución disponibles aún</Typography>
                  </Box>
                )}
              </Paper>
            </>
          )}
          {activeTab === 1 && (!inventory_age || !inventory_age.summary || inventory_age.summary.length === 0) && (
            <Paper className="p-24 mb-24 text-center shadow rounded-2xl">
              <Typography color="text.secondary">No hay datos de edad del inventario disponibles</Typography>
            </Paper>
          )}

          {/* Modal detalle edad inventario */}
          <Dialog open={ageDetailOpen} onClose={() => setAgeDetailOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                  {ageDetailData?.metric || ''} - Detalle
                </Typography>
                {ageDetailData && (
                  <Typography variant="caption" color="text.secondary">
                    {fmtNum(ageDetailData.total)} productos | Datos del {moment(ageDetailData.alert_date).format('DD/MM/YYYY')}
                  </Typography>
                )}
              </Box>
              <IconButton onClick={() => setAgeDetailOpen(false)} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent>
              {ageDetailLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Spinner show={true} color="#000" />
                </Box>
              ) : ageDetailData ? (
                <>
                  <TextField
                    size="small"
                    placeholder="Buscar por SKU o nombre..."
                    fullWidth
                    sx={{ mb: 2 }}
                    value={ageDetailSearch}
                    onChange={(e) => setAgeDetailSearch(e.target.value)}
                  />
                  <TableContainer sx={{ maxHeight: 500 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Producto</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Categoría</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Stock</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Costo</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Días en Alerta</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ageDetailData.products
                          .filter((p) => {
                            if (!ageDetailSearch) return true;
                            const q = ageDetailSearch.toLowerCase();
                            return p.sku.toLowerCase().includes(q) || p.name.toLowerCase().includes(q);
                          })
                          .map((p) => (
                            <TableRow key={p.sku}>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.sku}</TableCell>
                              <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</TableCell>
                              <TableCell>{p.category || '-'}</TableCell>
                              <TableCell align="right">{fmtNum(p.stock)}</TableCell>
                              <TableCell align="right">{fmtCLP(p.cost)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: daysColor(p.days_in_alert) }}>{p.days_in_alert}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No se pudieron cargar los datos</Typography>
              )}
            </DialogContent>
          </Dialog>

          {/* Modal detalle rotación */}
          <Dialog open={rotDetailOpen} onClose={() => setRotDetailOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{rotDetailData?.title || ''} - Detalle</Typography>
                {rotDetailData && (
                  <Typography variant="caption" color="text.secondary">
                    {fmtNum(rotDetailData.total)} productos | Últimos {rotDetailData.period_days} días
                  </Typography>
                )}
              </Box>
              <IconButton onClick={() => setRotDetailOpen(false)} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent>
              {rotDetailLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><Spinner show={true} color="#000" /></Box>
              ) : rotDetailData ? (
                <>
                  <TextField size="small" placeholder="Buscar por SKU o nombre..." fullWidth sx={{ mb: 2 }} value={rotDetailSearch} onChange={(e) => setRotDetailSearch(e.target.value)} />
                  <TableContainer sx={{ maxHeight: 500 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Producto</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Categoría</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Stock</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Vendido (30d)</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Venta Diaria</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Días Cobertura</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rotDetailData.products
                          .filter((p) => { if (!rotDetailSearch) return true; const q = rotDetailSearch.toLowerCase(); return p.sku.toLowerCase().includes(q) || p.name.toLowerCase().includes(q); })
                          .map((p) => (
                            <TableRow key={p.sku}>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.sku}</TableCell>
                              <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</TableCell>
                              <TableCell>{p.category || '-'}</TableCell>
                              <TableCell align="right">{fmtNum(p.stock)}</TableCell>
                              <TableCell align="right">{fmtNum(p.total_sold_30d)}</TableCell>
                              <TableCell align="right">{p.avg_daily_sales.toFixed(1)}</TableCell>
                              <TableCell align="right">{fmtNum(p.days_coverage)}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No se pudieron cargar los datos</Typography>
              )}
            </DialogContent>
          </Dialog>

          {/* Modal detalle productos */}
          <Dialog open={prodDetailOpen} onClose={() => setProdDetailOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{prodDetailData?.title || ''} - Detalle</Typography>
                {prodDetailData && (
                  <Typography variant="caption" color="text.secondary">{fmtNum(prodDetailData.total)} productos</Typography>
                )}
              </Box>
              <IconButton onClick={() => setProdDetailOpen(false)} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent>
              {prodDetailLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><Spinner show={true} color="#000" /></Box>
              ) : prodDetailData ? (
                <>
                  <TextField size="small" placeholder="Buscar por SKU o nombre..." fullWidth sx={{ mb: 2 }} value={prodDetailSearch} onChange={(e) => setProdDetailSearch(e.target.value)} />
                  <TableContainer sx={{ maxHeight: 500 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Producto</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Categoría</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Stock</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Costo</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Valor Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {prodDetailData.products
                          .filter((p) => { if (!prodDetailSearch) return true; const q = prodDetailSearch.toLowerCase(); return p.sku.toLowerCase().includes(q) || p.name.toLowerCase().includes(q); })
                          .map((p) => (
                            <TableRow key={p.sku}>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.sku}</TableCell>
                              <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</TableCell>
                              <TableCell>{p.category || '-'}</TableCell>
                              <TableCell align="right">{fmtNum(p.stock)}</TableCell>
                              <TableCell align="right">{fmtCLP(p.cost)}</TableCell>
                              <TableCell align="right">{fmtCLP(p.value)}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No se pudieron cargar los datos</Typography>
              )}
            </DialogContent>
          </Dialog>

          {/* SECCIÓN 4: Compras en Tránsito (Tab 2) */}
          {activeTab === 2 && transit && (
            <>
              <SectionTitle title="Compras en Tránsito" subtitle="¿Qué viene en camino?" />
              <motion.div className="grid grid-cols-1 sm:grid-cols-3 gap-16 mb-16" variants={container} initial="hidden" animate="show">
                <KpiCard label="Órdenes Activas" value={fmtNum(transit.active_orders)} icon="📋" />
                <KpiCard label="Unidades en Tránsito" value={fmtNum(transit.units_in_transit)} icon="🚚" />
                <KpiCard label="Próxima Recepción" value={transit.next_reception ? moment(transit.next_reception).format('DD/MM/YYYY') : 'N/A'} icon="📅" />
              </motion.div>
              {transit.orders_summary && transit.orders_summary.length > 0 && (
                <TableContainer component={Paper} className="shadow rounded-2xl mb-24">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Orden</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Items</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Unidades</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Fecha Estimada</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transit.orders_summary.map((o) => (
                        <TableRow key={o.order_number}>
                          <TableCell>{o.order_number}</TableCell>
                          <TableCell align="right">{fmtNum(o.items)}</TableCell>
                          <TableCell align="right">{fmtNum(o.units)}</TableCell>
                          <TableCell align="right">{moment(o.date_of_receipt).format('DD/MM/YYYY')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
          {activeTab === 2 && !transit && (
            <Paper className="p-24 mb-24 text-center shadow rounded-2xl">
              <Typography color="text.secondary">No hay datos de compras en tránsito disponibles</Typography>
            </Paper>
          )}

          {/* SECCIÓN 5: Distribución del Stock (Tab 0) */}
          {activeTab === 0 && distribution.stock_by_warehouse.length > 1 && (
            <>
              <SectionTitle title="Distribución del Stock" subtitle="¿Cómo se distribuye el inventario?" />
              <Paper className="p-16 shadow rounded-2xl mb-24">
                <Typography className="font-bold mb-8" color="text.secondary">Stock por Bodega</Typography>
                <ReactApexChart
                  options={{
                    chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit', foreColor: 'inherit' },
                    xaxis: { categories: distribution.stock_by_warehouse.map((w) => w.warehouse), labels: { style: { colors: theme.palette.text.secondary } } },
                    yaxis: { labels: { formatter: (v: number) => fmtNum(v), style: { colors: theme.palette.text.secondary } } },
                    tooltip: { y: { formatter: (v: number) => fmtNum(v) + ' unidades' } },
                    colors: [theme.palette.primary.main],
                    dataLabels: { enabled: false },
                    grid: { borderColor: theme.palette.divider },
                  }}
                  series={[{ name: 'Unidades', data: distribution.stock_by_warehouse.map((w) => w.units) }]}
                  type="bar"
                  height={300}
                />
              </Paper>
            </>
          )}

          {/* SECCIÓN 6: Rotación del Inventario (Tab 3) */}
          {activeTab === 3 && rotation && (rotation.top_fastest_rotation.length > 0 || rotation.top_slowest_rotation.length > 0) ? (
            <>
              <SectionTitle title="Rotación del Inventario" subtitle="¿Qué tan rápido se mueve el inventario? (últimos 30 días)" />
              <motion.div className="grid grid-cols-2 sm:grid-cols-4 gap-16 mb-16" variants={container} initial="hidden" animate="show">
                <KpiCard label="Productos con Ventas" value={fmtNum(rotation.total_products_with_sales)} icon="📈" color="#4caf50" />
                <KpiCard label="Productos sin Ventas" value={fmtNum(rotation.total_products_without_sales)} icon="📉" color="#ff9800" />
                <KpiCard label="Promedio Días de Cobertura" value={fmtNum(rotation.avg_days_coverage)} icon="📅" color="#2196f3" />
                <KpiCard label="Tasa de Rotación" value={rotation.rotation_rate != null ? rotation.rotation_rate.toFixed(2) : 'N/A'} icon="🔄" color="#7c4dff" />
              </motion.div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24">
                <Paper className="shadow rounded-2xl" sx={{ overflow: 'hidden' }}>
                  <TableContainer sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { height: 8 }, '&::-webkit-scrollbar-track': { background: '#e0e0e0', borderRadius: 4 }, '&::-webkit-scrollbar-thumb': { background: '#333', borderRadius: 4 }, scrollbarWidth: 'thin', scrollbarColor: '#333 #e0e0e0' }}>
                    <Typography className="font-bold p-16 pb-0" color="text.secondary">Top 10 Rotación Más Rápida</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>SKU</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Producto</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Stock</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Vendido (30d)</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Venta Diaria</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Días Cobertura</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(rotation.top_fastest_rotation || []).map((p) => (
                          <TableRow key={p.sku}>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.sku}</TableCell>
                            <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtNum(p.stock)}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtNum(p.total_sold_30d)}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{p.avg_daily_sales.toFixed(1)}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtNum(p.days_coverage)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ p: 1.5, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }} onClick={() => openRotationDetail('fastest')}>
                      Ver detalle completo →
                    </Typography>
                  </Box>
                </Paper>
                <Paper className="shadow rounded-2xl" sx={{ overflow: 'hidden' }}>
                  <TableContainer sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { height: 8 }, '&::-webkit-scrollbar-track': { background: '#e0e0e0', borderRadius: 4 }, '&::-webkit-scrollbar-thumb': { background: '#333', borderRadius: 4 }, scrollbarWidth: 'thin', scrollbarColor: '#333 #e0e0e0' }}>
                    <Typography className="font-bold p-16 pb-0" color="text.secondary">Top 10 Rotación Más Lenta</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>SKU</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Producto</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Stock</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Vendido (30d)</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Venta Diaria</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Días Cobertura</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(rotation.top_slowest_rotation || []).map((p) => (
                          <TableRow key={p.sku}>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.sku}</TableCell>
                            <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtNum(p.stock)}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtNum(p.total_sold_30d)}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{p.avg_daily_sales.toFixed(1)}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtNum(p.days_coverage)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ p: 1.5, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }} onClick={() => openRotationDetail('slowest')}>
                      Ver detalle completo →
                    </Typography>
                  </Box>
                </Paper>
              </div>
            </>
          ) : activeTab === 3 && rotation === null ? null : activeTab === 3 ? (
            <>
              <SectionTitle title="Rotación del Inventario" subtitle="¿Qué tan rápido se mueve el inventario? (últimos 30 días)" />
              <Paper className="p-24 mb-24 text-center shadow rounded-2xl">
                <Typography color="text.secondary">No hay datos de ventas disponibles para calcular la rotación</Typography>
              </Paper>
            </>
          ) : null}
        </div>
      }
      scroll={isMobile ? 'normal' : 'content'}
    />
  );
};

export default ReportsDashboard;
